import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/mechanic/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { HardHat, Plus, Pencil, Trash2 } from "lucide-react";

const blank = { name: "", hourly_rate: "", charge_rate: "", phone: "", specialty: "" };

export default function Mechanics() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const { data: techs = [] } = useQuery({ queryKey: ["mech-techs"], queryFn: async () => (await api.get("/techs")).data });

  const saveMut = useMutation({
    mutationFn: async () => {
      const body = { ...form, hourly_rate: Number(form.hourly_rate || 0), charge_rate: Number(form.charge_rate || 0) };
      if (editing) return api.put(`/techs/${editing.id}`, body);
      return api.post("/techs", body);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["mech-techs"] }); toast.success("Saved"); setOpen(false); },
    onError: () => toast.error("Failed to save"),
  });
  const delMut = useMutation({
    mutationFn: async (id) => api.delete(`/techs/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["mech-techs"] }); toast.success("Deleted"); },
  });

  const openNew = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ ...blank, ...t, hourly_rate: t.hourly_rate || "" }); setOpen(true); };

  return (
    <div className="p-8" data-testid="mech-mechanics-page">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-heading text-3xl mb-1">Mechanics</h1><p className="text-muted-foreground">Your technicians and labor rates.</p></div>
        <Button onClick={openNew} data-testid="new-tech-button"><Plus className="h-4 w-4 mr-2" />Add Mechanic</Button>
      </div>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Specialty</TableHead><TableHead>Phone</TableHead><TableHead className="text-right">Pay ($/hr)</TableHead><TableHead className="text-right">Bill ($/hr)</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {techs.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">No mechanics yet.</TableCell></TableRow>
            ) : techs.map((t) => (
              <TableRow key={t.id} data-testid={`tech-row-${t.id}`}>
                <TableCell className="font-semibold flex items-center gap-2"><HardHat className="h-4 w-4 text-muted-foreground" />{t.name}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{t.specialty || "—"}</TableCell>
                <TableCell className="text-sm">{t.phone || "—"}</TableCell>
                <TableCell className="text-right font-mono-data">{t.hourly_rate ? `$${t.hourly_rate}` : "—"}</TableCell>
                <TableCell className="text-right font-mono-data">{t.charge_rate ? `$${t.charge_rate}` : "—"}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(t)} data-testid={`edit-tech-${t.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => { if (window.confirm("Delete mechanic?")) delMut.mutate(t.id); }}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Mechanic" : "Add Mechanic"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2 space-y-2"><Label className="label-mono">Name *</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="tech-name" /></div>
            <div className="space-y-2"><Label className="label-mono">Pay rate ($/hr) <span className="text-xs text-muted-foreground">(what you pay)</span></Label><Input type="number" value={form.hourly_rate} onChange={(e) => set("hourly_rate", e.target.value)} data-testid="tech-rate" /></div>
            <div className="space-y-2"><Label className="label-mono">Bill rate ($/hr) <span className="text-xs text-muted-foreground">(customer rate)</span></Label><Input type="number" value={form.charge_rate} onChange={(e) => set("charge_rate", e.target.value)} data-testid="tech-charge-rate" /></div>
            <div className="space-y-2"><Label className="label-mono">Phone</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
            <div className="col-span-2 space-y-2"><Label className="label-mono">Specialty</Label><Input value={form.specialty} onChange={(e) => set("specialty", e.target.value)} placeholder="e.g. Engine, Transmission" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => saveMut.mutate()} disabled={!form.name || saveMut.isPending} data-testid="tech-save">{editing ? "Update" : "Add"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
